import { Card, CardBody } from "@heroui/react";
import React from "react";

import { useAuth } from "@wc/domain/authn";
import { PermissionsEnum, userHasPermissionInTeam } from "@wc/domain/authz";
import { formatDate } from "@wc/domain/dates";
import EditableInput from "@wc/ui/components/editable-input";

import useTeam from "../hooks/use-team";

/**
 * A page dedicated to displaying team information
 * @param {object} props The component props
 * @param {boolean} props.readOnly Whether to allow editing or not, regardless of permissions
 * @returns {React.ReactElement} The component
 */
export default function TeamInfo({ readOnly = true }) {
  const teamData = useTeam();
  const userData = useAuth();
  const [hasManagePermission, setHasManagePermission] = React.useState(false);

  React.useEffect(() => {
    const fetchPermissions = async () => {
      if (readOnly) {
        setHasManagePermission(false);
        return;
      }

      try {
        const hasPerm = await userHasPermissionInTeam(
          userData.getCurrentUser(),
          teamData?.team.id,
          PermissionsEnum.MANAGE_TEAM,
        );
        setHasManagePermission(hasPerm);
      } catch (err) {
        console.error(err);
        setHasManagePermission(false);
      }
    };

    if (teamData?.team.id && userData.getCurrentUser()) {
      fetchPermissions();
    }
  }, [teamData?.team.id, userData, readOnly]);

  return (
    <Card className="p-3">
      <CardBody>
        <ul className="flex flex-col gap-4">
          <li className="flex items-center gap-4">
            <b>Name: </b>
            <EditableInput
              isDisabled={!hasManagePermission}
              value={teamData.team.name}
              onSave={async (newName) => await teamData.updateTeamName(newName)}
            />
          </li>
          <li className="flex gap-4">
            <b>Created: </b>
            {formatDate(teamData.team.createdAt)}
          </li>
        </ul>
      </CardBody>
    </Card>
  );
}
